import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

export const BlockchainEvolutionSlide = ({ slide }) => {
  const phaseColors = ['#8b5cf6', '#0068ff', '#22c55e'];
  const phaseIcons = [
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ];

  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#030308] via-[#060612] to-[#030308]" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#8b5cf6] opacity-[0.05] blur-[150px] rounded-full" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#0068ff] opacity-[0.05] blur-[150px] rounded-full" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-[#22c55e] opacity-[0.04] blur-[150px] rounded-full" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.02]" viewBox="0 0 1920 1080">
          <line x1="640" y1="540" x2="1280" y2="540" stroke="#0068ff" strokeWidth="2" strokeDasharray="8 8" />
        </svg>
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 py-4">
        <SectionLabel delay={0}>Blockchain Roadmap</SectionLabel>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mt-4 mb-8 animate-slide-up">{slide.title}</h2>

        <div className="flex items-stretch gap-4 w-full max-w-[1100px] mb-6">
          {slide.phases.map((phase, idx) => {
            const color = phaseColors[idx];
            const isLast = idx === slide.phases.length - 1;
            return (
              <React.Fragment key={idx}>
                <GlassCard delay={0.15 + idx * 0.12} className="flex-1 p-5 flex flex-col" style={{
                  background: `linear-gradient(180deg, ${color}0a, transparent)`,
                  borderColor: `${color}${isLast ? '40' : '25'}`,
                  boxShadow: isLast ? `0 0 40px ${color}12` : 'none'
                }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, color }}>{phaseIcons[idx]}</div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color }}>{phase.label}</div>
                      <div className="text-white font-bold text-base">{phase.name}</div>
                    </div>
                  </div>
                  <p className="text-white/35 text-xs leading-relaxed mb-3">{phase.description}</p>
                  <div className="space-y-1.5 mb-3 flex-1">
                    {phase.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full shrink-0" style={{ background: color }} />
                        <span className="text-white/55 text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-white/[0.06]">
                    <div className="text-[9px] uppercase tracking-widest text-white/25 mb-1">Outcome</div>
                    <span className="text-white/80 text-xs font-semibold">{phase.outcome}</span>
                  </div>
                </GlassCard>
                {idx < slide.phases.length - 1 && (
                  <div className="flex items-center justify-center shrink-0 self-center">
                    <svg viewBox="0 0 32 16" className="w-7 h-4">
                      <defs><marker id={`evArr${idx}`} markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><polygon points="0 0,6 2,0 4" fill={phaseColors[idx + 1]} opacity="0.7"/></marker></defs>
                      <path d="M 2 8 L 26 8" fill="none" stroke={phaseColors[idx + 1]} strokeWidth="1.5" strokeOpacity="0.5" markerEnd={`url(#evArr${idx})`} />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mb-5 animate-slide-up" style={{ animationDelay: '0.5s' }}>
          {slide.phases.map((phase, idx) => {
            const color = phaseColors[idx];
            return (
              <React.Fragment key={idx}>
                <div className="px-4 py-2 rounded-xl text-center" style={{
                  background: `${color}10`, border: `1px solid ${color}30`
                }}>
                  <span className="text-xs font-bold" style={{ color }}>{phase.name}</span>
                </div>
                {idx < slide.phases.length - 1 && (
                  <svg viewBox="0 0 24 12" className="w-5 h-2.5"><path d="M 2 6 L 18 6" fill="none" stroke="white" strokeWidth="1" strokeOpacity="0.15" /><path d="M 15 3 L 20 6 L 15 9" fill="none" stroke="white" strokeWidth="1" strokeOpacity="0.15" /></svg>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <GlassCard delay={0.6} className="px-8 py-3 text-center max-w-2xl" style={{ borderColor: 'rgba(0,104,255,0.2)' }}>
          <span className="text-white/50 text-sm">{slide.closing[0]}</span>
          <br />
          <span className="text-[#0068ff] text-sm font-semibold">{slide.closing[1]}</span>
        </GlassCard>
      </div>
    </SlideWrapper>
  );
};
